import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  fetchAnswerFileBlobUrl,
  guessIsImageFromToken,
  isImageMimeType,
} from "@/lib/answer-file";
import { ChevronLeft, ChevronRight, FileText, Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

export type CommentAttachmentPreview = {
  id?: string;
  filename: string;
  object_key: string;
  content_type?: string | null;
};

type LoadedAttachment = CommentAttachmentPreview & {
  blobUrl: string | null;
  fetchedContentType: string | null;
  loading: boolean;
  error: boolean;
};

function isImageAttachment(attachment: LoadedAttachment): boolean {
  return (
    isImageMimeType(
      attachment.fetchedContentType ?? attachment.content_type ?? null,
    ) || guessIsImageFromToken(attachment.filename)
  );
}

export function CommentAttachmentsGallery({
  attachments,
}: {
  attachments: CommentAttachmentPreview[];
}) {
  const [loaded, setLoaded] = useState<LoadedAttachment[]>([]);
  const [activeImageKey, setActiveImageKey] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const objectUrls: string[] = [];
    setLoaded(
      attachments.map((attachment) => ({
        ...attachment,
        blobUrl: null,
        fetchedContentType: null,
        loading: true,
        error: false,
      })),
    );

    for (const [index, attachment] of attachments.entries()) {
      fetchAnswerFileBlobUrl(attachment.object_key)
        .then(({ blobUrl, contentType }) => {
          if (cancelled) {
            URL.revokeObjectURL(blobUrl);
            return;
          }
          objectUrls.push(blobUrl);
          setLoaded((current) =>
            current.map((item, itemIndex) =>
              itemIndex === index
                ? {
                    ...item,
                    blobUrl,
                    fetchedContentType: contentType,
                    loading: false,
                  }
                : item,
            ),
          );
        })
        .catch(() => {
          if (cancelled) return;
          setLoaded((current) =>
            current.map((item, itemIndex) =>
              itemIndex === index
                ? { ...item, loading: false, error: true }
                : item,
            ),
          );
        });
    }

    return () => {
      cancelled = true;
      for (const url of objectUrls) URL.revokeObjectURL(url);
    };
  }, [attachments]);

  const images = useMemo(
    () =>
      loaded.filter(
        (attachment) =>
          isImageAttachment(attachment) &&
          attachment.blobUrl != null &&
          !attachment.error,
      ),
    [loaded],
  );
  const activeIndex = Math.max(
    0,
    images.findIndex((attachment) => attachment.object_key === activeImageKey),
  );
  const activeImage = activeImageKey ? (images[activeIndex] ?? null) : null;

  const move = (direction: -1 | 1) => {
    if (images.length < 2) return;
    const nextIndex = (activeIndex + direction + images.length) % images.length;
    setActiveImageKey(images[nextIndex].object_key);
  };

  if (attachments.length === 0) return null;

  return (
    <>
      <div className="mt-3 flex flex-wrap gap-3">
        {loaded.map((attachment) => {
          const image = isImageAttachment(attachment);
          if (attachment.loading) {
            return (
              <div
                key={attachment.object_key}
                className="flex h-36 w-52 items-center justify-center rounded-md border bg-muted/30"
                aria-label={`Loading ${attachment.filename}`}
              >
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            );
          }
          if (image && attachment.blobUrl) {
            return (
              <button
                key={attachment.object_key}
                type="button"
                className="group overflow-hidden rounded-md border bg-muted/30 text-left shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => setActiveImageKey(attachment.object_key)}
                aria-label={`View ${attachment.filename}`}
              >
                <img
                  src={attachment.blobUrl}
                  alt={attachment.filename}
                  className="h-44 w-64 object-cover transition-transform group-hover:scale-[1.02]"
                />
                <span className="block max-w-64 truncate px-2 py-1 text-xs text-muted-foreground">
                  {attachment.filename}
                </span>
              </button>
            );
          }
          return (
            <Button
              key={attachment.object_key}
              type="button"
              variant="outline"
              size="sm"
              disabled={!attachment.blobUrl}
              asChild={Boolean(attachment.blobUrl)}
            >
              {attachment.blobUrl ? (
                <a
                  href={attachment.blobUrl}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`Open ${attachment.filename}`}
                >
                  <FileText className="h-4 w-4" />
                  {attachment.filename}
                </a>
              ) : (
                <>
                  <FileText className="h-4 w-4" />
                  {attachment.filename}
                </>
              )}
            </Button>
          );
        })}
      </div>

      <Dialog
        open={activeImage != null}
        onOpenChange={(open) => {
          if (!open) setActiveImageKey(null);
        }}
      >
        <DialogContent
          elevated
          className="max-h-[96vh] max-w-[min(96vw,72rem)] sm:max-w-[min(96vw,72rem)]"
          onKeyDown={(event) => {
            if (event.key === "ArrowLeft") move(-1);
            if (event.key === "ArrowRight") move(1);
          }}
        >
          <DialogHeader>
            <DialogTitle>
              {activeImage?.filename ?? "Comment Image"}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Full-size comment image
            </DialogDescription>
          </DialogHeader>
          {activeImage ? (
            <div className="relative flex min-h-0 items-center justify-center">
              {images.length > 1 ? (
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="absolute left-2 z-10 rounded-full bg-background/90"
                  onClick={() => move(-1)}
                  aria-label="Previous Image"
                >
                  <ChevronLeft className="h-5 w-5" />
                </Button>
              ) : null}
              <img
                src={activeImage.blobUrl ?? undefined}
                alt={activeImage.filename}
                className="max-h-[76vh] max-w-full object-contain"
              />
              {images.length > 1 ? (
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="absolute right-2 z-10 rounded-full bg-background/90"
                  onClick={() => move(1)}
                  aria-label="Next Image"
                >
                  <ChevronRight className="h-5 w-5" />
                </Button>
              ) : null}
            </div>
          ) : null}
          {images.length > 1 ? (
            <p className="text-center text-sm text-muted-foreground">
              {activeIndex + 1} of {images.length}
            </p>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}
